import React from 'react';

import {
    getSquareOnlineStoreBrief,
    getSquareOnlineStoreBriefVariables,
} from '__generated__/getSquareOnlineStoreBrief';
import { SquareOnlineStoreBriefFields } from '__generated__/SquareOnlineStoreBriefFields';
import { AuthConsumer } from 'projects/components/AuthContext';
import DesignerMetaContext, { DesignerMetaLayouts } from 'projects/components/DesignerMetaContext';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesignersPage from 'projects/routes/InviteDesignersPage';
import AboutYouPage from 'projects/routes/squareOnlineStore/AboutYouPage';
import DetailsPage from 'projects/routes/squareOnlineStore/DetailsPage';
import SummaryPage from 'projects/routes/squareOnlineStore/SummaryPage';
import TimeAndBudgetPage from 'projects/routes/squareOnlineStore/TimeAndBudgetPage';
import { Industries } from 'projects/util/industries';
import { createStepState, StepConfiguration } from 'projects/util/step';
import { ApolloConsumer } from 'react-apollo';
import { RouteComponentProps, withRouter } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import SquareAuthModal from './SquareAuthModal';
import SquareAuthModalDirect from './SquareAuthModalDirect';
import queries from './SquareOnlineStoreBrief.graphql';
import WelcomeOverlay from './WelcomeOverlay';

type Props = RouteComponentProps<{
    briefId: string;
    step: string;
}>;

const steps: StepConfiguration[] = [
    {
        step: 1,
        title: __('Tell us about your project'),
    },
    { step: 2, title: __('Your time & budget') },
    { step: 3, title: __('About you') },
    { step: 4, title: __('Review your brief') },
    {
        step: 5,
        title: __('Invite designers to quote'),
        requiresAuth: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

export const SquareOnlineStoreBrief: React.FunctionComponent<Props> = props => (
    <BriefQuery<getSquareOnlineStoreBrief, getSquareOnlineStoreBriefVariables>
        query={queries.getSquareOnlineStoreBrief}
        variables={{ id: props.match.params.briefId }}
        fieldsTypename="SquareOnlineStoreBriefFields"
    >
        {(data, refetch) => {
            const step = createStepState({
                steps,
                brief: data.brief,
                currentStep: parseInt(props.match.params.step, 10),
                designerSources: data.brief.designersWithSource.map(d => d.source),
            });

            const brief = {
                ...data.brief,
                fields: data.brief.fields as SquareOnlineStoreBriefFields,
            };

            const industries = new Industries(data.industries);

            return (
                <ApolloConsumer>
                    {client => (
                        <AuthConsumer>
                            {({ login }) => (
                                <Brief
                                    refetch={refetch}
                                    schema="squareOnlineStore"
                                    brief={brief}
                                    viewer={data.viewer}
                                    login={login}
                                    step={step}
                                    client={client}
                                    recommendedAuthModal={SquareAuthModal}
                                    directAuthModal={SquareAuthModalDirect}
                                >
                                    {form => (
                                        <Switch>
                                            <Route
                                                path={`/briefs/square-online-store/:briefId/1`}
                                                render={() => (
                                                    <>
                                                        <WelcomeOverlay brief={brief} />
                                                        <DetailsPage form={form} brief={brief} />
                                                    </>
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/square-online-store/:briefId/2`}
                                                render={() => (
                                                    <TimeAndBudgetPage form={form} brief={brief} />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/square-online-store/:briefId/3`}
                                                render={() => (
                                                    <AboutYouPage
                                                        form={form}
                                                        industries={data.industries}
                                                        brief={brief}
                                                    />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/square-online-store/:briefId/4`}
                                                render={() => (
                                                    <SummaryPage
                                                        brief={brief}
                                                        industries={industries}
                                                    />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/square-online-store/:briefId/5`}
                                                render={() => (
                                                    <DesignerMetaContext.Provider
                                                        value={{
                                                            layout: DesignerMetaLayouts.compact,
                                                            hasMoreResultsUrl: false,
                                                            hasResponsiveness: false,
                                                        }}
                                                    >
                                                        <InviteDesignersPage
                                                            brief={brief}
                                                            viewer={data.viewer}
                                                        >
                                                            {__(
                                                                'Here are the Square Online Store experts that best match your needs'
                                                            )}
                                                        </InviteDesignersPage>
                                                    </DesignerMetaContext.Provider>
                                                )}
                                            />
                                        </Switch>
                                    )}
                                </Brief>
                            )}
                        </AuthConsumer>
                    )}
                </ApolloConsumer>
            );
        }}
    </BriefQuery>
);

export default withRouter(SquareOnlineStoreBrief);
